import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ArrowLeft, RefreshCcw, Calendar, User, Users, Camera, FileText, AlertCircle, Check, X, AlertTriangle, Copy, Activity, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../components/ui/use-toast';
import SmartImage from "../../components/SmartImage";
import CddTierCard from './components/CddTierCard';
import OcrDataPanel from "../../components/OcrDataPanel";
import DocumentDetailPanel from "../../components/DocumentDetailPanel";
import TransactionGamingProfile from "../../components/TransactionGamingProfile";
import { getApiUrl, getServerName, getSystemID, getLocalApiUrl, authFetch } from '../../config/api';
import { getConfidenceLevelFromScore, getProfileConfidenceConfig, getProfileConfidenceBadgeClasses } from '../../lib/profileConfidence';
import AmlCategoryResultsTabs from '../../components/aml/AmlCategoryResultsTabs';
import { sanitizeVendorBranding } from '../../utils/sanitizeVendorBranding';
import { flattenResultsByCategory, formatThreatLevel, normalizeProfileDob } from '../../utils/amlResultCategories';

/** When dedicated photo B64 fields are empty, reuse licence image from OCR pages (data URLs). */
function firstOcrDataUrl(ocr) {
  if (!Array.isArray(ocr)) return '';
  for (const p of ocr) {
    const u = p?.PageURL ?? p?.pageURL;
    if (typeof u === 'string' && u.trim().startsWith('data:')) return u.trim();
  }
  return '';
}

// Helper function to get confidence styling for faceprints
const getFaceprintConfidenceStyle = (confidence) => {
  const threshold = 0.75; // Standard threshold for faceprints (75%)
  const isAboveThreshold = confidence >= threshold;
  
  return {
    variant: isAboveThreshold ? "outline" : "destructive",
    className: isAboveThreshold
      ? "bg-green-100 text-green-800 border-green-200"
      : "bg-red-100 text-red-800 border-red-200",
    percentage: Math.round(confidence * 100)
  };
};

// Helper function to get DVS Status badge styling
const getDVSStatusBadgeStyle = (status) => {
  switch (status?.toUpperCase()) {
    case 'VERIFIED':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'READY':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'PENDING':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'REJECTED':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'NOT READY':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

// AML/PEP badge styling
const getAmlStatusBadgeStyle = (category) => {
  switch ((category || '').toUpperCase()) {
    case 'PEP':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'RCA':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'SIP':
    case 'SPECIAL INTEREST PERSON':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'SIE':
    case 'SPECIAL INTEREST ENTITY':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'MATCH':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getAmlCategoryFromSample = (sample) => {
  if (!Array.isArray(sample) || sample.length === 0) return null;
  const firstWithCategory = sample.find((r) => r && (r.riskCategory || r.category));
  return (firstWithCategory?.riskCategory || firstWithCategory?.category || '').toString() || null;
};

// Helper function to format dates consistently
const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return 'Unknown';
  
  try {
    const date = new Date(dateTimeStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    // Check if the original string contains time information
    const hasTime = dateTimeStr.includes('T') || dateTimeStr.includes(':') || dateTimeStr.includes(' ');
    
    if (hasTime && (date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0)) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
    } else {
      return `${day} ${month} ${year}`;
    }
  } catch {
    return dateTimeStr; // Return as-is if parsing fails
  }
};

const ProfileDetail = ({ profileId, onBack }) => {
  const { toast } = useToast();
  const [profileData, setProfileData] = useState(null);
  const [patronProfile, setPatronProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [membershipData, setMembershipData] = useState(null);
  const [isLoadingMembership, setIsLoadingMembership] = useState(false);
  const [showMembershipPanel, setShowMembershipPanel] = useState(false);
  const [isRunningAmlCheck, setIsRunningAmlCheck] = useState(false);
  const [amlChecks, setAmlChecks] = useState([]);
  const [otherDocuments, setOtherDocuments] = useState([]);
  const [isAmlModalOpen, setIsAmlModalOpen] = useState(false);
  const [amlModalData, setAmlModalData] = useState(null);
  const [selectedAmlIndex, setSelectedAmlIndex] = useState(0);
  const [amlHeaderStatus, setAmlHeaderStatus] = useState(null);
  const [showTransactionGamingProfile, setShowTransactionGamingProfile] = useState(false);
  const [caseHistory, setCaseHistory] = useState([]);
  const [isLoadingCases, setIsLoadingCases] = useState(false);
  const [isRiskAssessmentExpanded, setIsRiskAssessmentExpanded] = useState(false);
  const [riskRecalculating, setRiskRecalculating] = useState(false);

  // Copy to clipboard function
  const copyToClipboard = (text, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: 'Copied to clipboard',
        description: `Profile ID: ${text}`,
        duration: 2000,
      });
    }).catch(() => {
      toast({
        title: 'Copy failed',
        description: 'Unable to copy to clipboard',
        variant: 'destructive',
        duration: 2000,
      });
    });
  };

  // Removed fetchFileData function - now using SmartImage for OCR images

  // Add to Watchlist (person of interest)
  const addToWatchlist = async () => {
    try {
      const memberId = profileData?.MID || String(profileId)
      const firstName = profileData?.FN || ''
      const lastName = profileData?.LN || ''
      const dob = profileData?.DOB || ''
      const payload = {
        member_id: memberId,
        alert_type: 'POI',
        severity: 'medium',
        status: 'open',
        notes: `Added from Profile detail${firstName || lastName ? ` — ${[firstName, lastName].filter(Boolean).join(' ')}` : ''}${dob ? ` (DOB ${dob})` : ''}`,
      }
      const resp = await authFetch(getLocalApiUrl('/api/watchlist'), { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify(payload) })
      const json = await resp.json()
      if (!resp.ok || !json.success) throw new Error(json.error || 'Failed to add to watchlist')
      toast({ title: 'Added to Watchlist', description: `${memberId} — ${firstName} ${lastName}` })
    } catch (e) {
      toast({ title: 'Add to Watchlist failed', description: e.message || 'Unknown error', variant: 'destructive' })
    }
  }

  // Function to fetch membership details - uses patron/resolve API (same format as payout mobile)
  const fetchMembershipDetails = async () => {
    const memberId = profileData?.MID || profileData?.MasterProfileID
    if (!memberId) {
      toast({
        title: "Error",
        description: "Member ID not found",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingMembership(true);
    
    try {
      // Use the patron/resolve API (same response format as mobile payout)
      const response = await authFetch(getLocalApiUrl('/api/patron/resolve'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_id: memberId,
          lookup_type: 'member_id'
        })
      });
      
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status} ${response.statusText}`);
      }

      const json = await response.json();
      console.log('Patron Resolve Response:', json);

      if (!json.success || !json.data?.resolved || !json.data?.patron) {
        toast({
          title: 'Membership details',
          description: json.data?.message || 'No membership record found for this profile.',
          variant: 'destructive'
        });
        setMembershipData(null);
        setShowMembershipPanel(true);
        return;
      }

      // Map patron/resolve data to PatronDetails format for display
      const patron = json.data.patron;
      const mappedData = {
        ResponseCode: '200',
        ResponseMessage: 'Success',
        VersionNo: '1.0',
        PatronDetails: {
          MemberID: json.data.member_id || memberId,
          MasterProfileID: json.data.master_profile_id || profileData?.MasterProfileID,
          AccountID: profileData?.AID || '',
          FirstName: patron.firstname || profileData?.FN || '',
          LastName: patron.lastname || profileData?.LN || '',
          DateOfBirth: patron.dob || profileData?.DOB || '',
          Gender: patron.gender || '',
          Occupation: patron.occupation || '',
          Email: patron.email || '',
          MobilePhone: patron.mobile_phone || '',
          Address1: patron.address1 || '',
          Address2: patron.address2 || '',
          Suburb: patron.suburb || '',
          State: patron.state || '',
          PostCode: patron.postal_code || '',
          TierLevel: patron.tier_level || '',
          MembershipStatus: patron.status || 'ACTIVE',
          MembershipType: json.data.patron_kind?.toUpperCase() || 'MEMBER',
          HomeSite: '',
          DateJoined: patron.join_date || '',
          LastVisitDate: '',
          IDDocumentType: '',
          IDDocumentNumber: '',
          CountryofResidence: '',
          CountryofCitizenship: '',
          CardNumber: json.data.visitor_id || json.data.card_id || '',
          RiskScore: patron.risk_score,
          RiskLevel: patron.risk_level,
          OnWatchlist: json.data.on_watchlist,
          WatchlistAlert: json.data.watchlist_alert,
        }
      };

      console.log('Mapped Membership Data:', mappedData);
      setMembershipData(mappedData);
      setShowMembershipPanel(true);

    } catch (error) {
      console.error('Membership Details Error:', error);
      setMembershipData(null);
      toast({
        title: 'Membership details failed',
        description: error?.message || 'Could not load membership details',
        variant: 'destructive'
      });
      setShowMembershipPanel(true);
    } finally {
      setIsLoadingMembership(false);
    }
  };

  const openAmlResultsModal = (payload) => {
    const flat = flattenResultsByCategory(payload.results, payload.resultsByCategory);
    setAmlModalData({
      ...payload,
      results: flat.length > 0 ? flat : payload.results || []
    });
    setIsAmlModalOpen(true);
    setSelectedAmlIndex(0);
  };

  // Run AML/PEP check: strict (DOB) first, then relax to name+country, then name-only when no results
  const runAmlPepCheck = async () => {
    if (!profileData) return;
    const getDetailValue = (list, fieldName) => {
      if (!Array.isArray(list)) return null;
      const row = list.find(r => (r?.Name || '').toLowerCase() === fieldName.toLowerCase());
      const val = row?.Content;
      return typeof val === 'string' && val.trim().length ? val.trim() : null;
    };
    const getDocNumber = (list) => getDetailValue(list, 'DocumentNumber');
    const issueCountry = getDetailValue(profileData.CreatedDocumentDetails, 'IssueCountry')
      || getDetailValue(profileData.RecentDocumentDetails, 'IssueCountry')
      || null;
    const fallbackCountry = issueCountry || undefined;
    const toCountryCode = (country) => {
      if (!country) return '';
      const c = country.trim().toUpperCase();
      const map = {
        'AUSTRALIA': 'AUST',
        'UNITED STATES': 'USA',
        'UNITED STATES OF AMERICA': 'USA',
        'UNITED KINGDOM': 'UK',
        'NEW ZEALAND': 'NZ',
        'PAKISTAN': 'PAKIS',
        'IRAN': 'IRAN',
        'INDIA': 'INDIA',
        'CHINA': 'CHINA'
      };
      if (map[c]) return map[c];
      const letters = c.replace(/[^A-Z]/g, '');
      return letters.slice(0, 5);
    };
    try {
      setIsRunningAmlCheck(true);
      const createdId = getDocNumber(profileData.CreatedDocumentDetails);
      const recentId = getDocNumber(profileData.RecentDocumentDetails);
      const docIds = [createdId, recentId].filter(Boolean);
      const documents = { document_ids: docIds };
      const patronDob = normalizeProfileDob(profileData.DOB);
      const countryCode = toCountryCode(fallbackCountry);
      const memberPayload = {
        member_id: profileData.MID || profileId,
        name: `${profileData.FN || ''} ${profileData.LN || ''}`.trim(),
        performed_by: 'web_user'
      };

      const postAmlCheck = async (fields, reason) => {
        const resp = await authFetch(`${getLocalApiUrl('/api/aml/checks/run')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...memberPayload, ...fields, reason })
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data?.success) {
          throw new Error(data?.error || 'AML check failed');
        }
        return data;
      };

      const finishCheck = async (responseData, toastTitle) => {
        const count = responseData?.data?.result_count ?? 0;
        toast({ title: toastTitle, description: `Matches found: ${count}` });
        await fetchAmlChecks();
        openAmlResultsModal({
          checkId: Number(responseData?.data?.id),
          name: String(memberPayload.name),
          country: countryCode || undefined,
          results: Array.isArray(responseData?.data?.sample) ? responseData.data.sample : [],
          resultsByCategory: responseData?.data?.resultsByCategory || null,
          provider: responseData?.data?.provider,
          greenidVerificationId: responseData?.data?.greenidVerificationId,
          greenidOverallStatus: responseData?.data?.greenidOverallStatus,
          screeningSummary: responseData?.data?.screeningSummary || null
        });
        try {
          const cat = getAmlCategoryFromSample(
            Array.isArray(responseData?.data?.sample) ? responseData.data.sample : []
          );
          setAmlHeaderStatus(cat || (count > 0 ? 'MATCH' : 'N/A'));
        } catch { /* ignore */ }
        return count;
      };

      const strictFields = {
        country: countryCode || undefined,
        date_of_birth: patronDob || undefined
      };

      // 1. With document IDs when available (audit trail; same search criteria as step 2)
      if (docIds.length > 0) {
        const withDoc = await postAmlCheck(
          { ...strictFields, documents },
          'Profile detail on-demand AML/PEP check (with document)'
        );
        if ((withDoc?.data?.result_count ?? 0) > 0) {
          await finishCheck(withDoc, 'AML/PEP Check Completed');
          return;
        }
      }

      // 2. Name + country + DOB
      const strict = await postAmlCheck(strictFields, 'Profile detail on-demand AML/PEP check');
      if ((strict?.data?.result_count ?? 0) > 0) {
        await finishCheck(strict, 'AML/PEP Check Completed');
        return;
      }

      // 3. Name + country (no DOB — Dow Jones records often lack DOB)
      const nameCountry = await postAmlCheck(
        { country: countryCode || undefined },
        'Fallback AML/PEP check (name+country) after no matches with DOB'
      );
      if ((nameCountry?.data?.result_count ?? 0) > 0) {
        await finishCheck(nameCountry, 'AML/PEP Check Completed (Fallback)');
        return;
      }

      // 4. Name only (aligns with AML Screening patron search)
      const nameOnly = await postAmlCheck(
        {},
        'Fallback AML/PEP check (name only) after no matches with country'
      );
      await finishCheck(nameOnly, 'AML/PEP Check Completed (Fallback)');
    } catch (e) {
      toast({ title: 'AML/PEP Check Failed', description: e.message || 'Unknown error', variant: 'destructive' });
    } finally {
      setIsRunningAmlCheck(false);
    }
  };

  // Load AML check history for this member.
  // Use profileData.MID only — aml_checks stores member_id (e.g. 3022), not master_profile_id (MP003022).
  // profileId from search is MasterProfileID; using it would return no results.
  const fetchAmlChecks = async () => {
    const memberId = profileData?.MID;
    if (!memberId) return;
    try {
      const resp = await authFetch(getLocalApiUrl(`/api/aml/checks?member_id=${encodeURIComponent(memberId)}&limit=10`));
      const data = await resp.json();
      if (data && data.success) setAmlChecks(data.data || []);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => { fetchAmlChecks(); }, [profileData?.MID]);

  // Fetch ECDD/SMR case history when profile has Member ID
  useEffect(() => {
    const memberId = profileData?.MID;
    if (!memberId) {
      setCaseHistory([]);
      return;
    }
    const fetchCases = async () => {
      setIsLoadingCases(true);
      try {
        const res = await authFetch(getLocalApiUrl(`/api/cases?member_id=${encodeURIComponent(memberId)}&limit=50`));
        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          const relevant = json.data.filter((c) =>
            ['SMR', 'ECDD', 'SUSPICIOUS', 'INVESTIGATION'].includes(String(c.case_type || '').toUpperCase())
          );
          setCaseHistory(relevant);
        } else {
          setCaseHistory([]);
        }
      } catch (_) {
        setCaseHistory([]);
      } finally {
        setIsLoadingCases(false);
      }
    };
    fetchCases();
  }, [profileData?.MID]);

  // Load profile documents (identity_documents + onboarding + KYC attachments)
  useEffect(() => {
    const loadDocs = async () => {
      const candidates = [
        profileData?.MasterProfileID,
        profileData?.MID,
        profileId,
      ].filter(Boolean);
      const id = candidates.find((c) => String(c).trim()) || null;
      if (!id) return;
      try {
        const resp = await authFetch(
          getLocalApiUrl(`/api/profile/${encodeURIComponent(String(id))}/documents`)
        );
        const data = await resp.json();
        if (data && data.success) setOtherDocuments(data.data || []);
      } catch (_) {
        setOtherDocuments([]);
      }
    };
    if (!profileData) return;
    loadDocs();
  }, [profileData?.MasterProfileID, profileData?.MID, profileId]);

  // Open AML check modal from URL param (?aml_check_id=123)
  useEffect(() => {
    const url = new URL(window.location.href);
    const idParam = url.searchParams.get('aml_check_id');
    if (idParam) {
      const idNum = Number(idParam);
      if (!Number.isNaN(idNum)) {
        void viewAmlCheckDetails(idNum);
      }
    }
  }, []);

  // Helper: View details of a specific AML check
  const viewAmlCheckDetails = async (checkId) => {
    try {
      const resp = await authFetch(getLocalApiUrl(`/api/aml/checks/${encodeURIComponent(String(checkId))}`))
      const data = await resp.json();
      if (resp.ok && data?.success && data.data) {
        const row = data.data || {};
        const results = Array.isArray(row.result_sample) ? row.result_sample : [];
        openAmlResultsModal({
          checkId,
          name: String(row.full_name || ''),
          country: String(row.country_code || ''),
          results
        });
        return;
      }
      // Fallback: if backend details not found (e.g., 404), try re-running an advanced search from listing metadata
      const meta = amlChecks.find(c => c.id === checkId);
      if (!meta || !meta.full_name) throw new Error(data?.error || 'Check not found');
      const params = new URLSearchParams({
        name: meta.full_name,
        country: meta.country_code || '',
        includeInactive: 'true',
        limit: '50'
      });
      const resp2 = await authFetch(getLocalApiUrl(`/api/aml/search/persons/advanced?${params}`));
      const data2 = await resp2.json();
      if (!resp2.ok) throw new Error(data2?.error || 'Failed to search for results');
      const results = (data2 && data2.data && (Array.isArray(data2.data.results) ? data2.data.results : data2.data)) || [];
      openAmlResultsModal({
        checkId,
        name: String(meta.full_name),
        country: meta.country_code || '',
        results: Array.isArray(results) ? results : [],
        resultsByCategory: data2?.data?.resultsByCategory || null,
        provider: data2?.data?.provider,
        greenidVerificationId: data2?.data?.greenidVerificationId,
        greenidOverallStatus: data2?.data?.greenidOverallStatus,
        screeningSummary: data2?.data?.screeningSummary || null
      });
    } catch (e) {
      toast({ title: 'Unable to load AML Check', description: e.message || 'Unknown error', variant: 'destructive' });
    }
  };

  // After list loads, fetch latest AML status for header badge
  useEffect(() => {
    const loadLatest = async () => {
      if (!amlChecks || amlChecks.length === 0) { setAmlHeaderStatus(null); return; }
      try {
        const latest = amlChecks[0];
        const resp = await authFetch(getLocalApiUrl(`/api/aml/checks/${encodeURIComponent(String(latest.id))}`))
        const data = await resp.json();
        if (resp.ok && data?.success && data.data) {
          const sample = Array.isArray(data.data.result_sample) ? data.data.result_sample : [];
          const cat = getAmlCategoryFromSample(sample);
          setAmlHeaderStatus(cat || (Number(data.data.result_count) > 0 ? 'MATCH' : 'N/A'));
        } else {
          setAmlHeaderStatus(latest.result_count > 0 ? 'MATCH' : 'N/A');
        }
      } catch (_) {
        setAmlHeaderStatus(null);
      }
    };
    loadLatest();
  }, [amlChecks]);


  useEffect(() => {
    const fetchProfileData = async () => {
      setIsLoading(true);
      
      try {
        // Load from API
        console.log('Loading data from API');
        
        const requestBody = {
          ServerName: getServerName(),
          SystemID: getSystemID(),
          Purpose: "GETPROFILE",
          MasterProfileID: profileId,
          Source: "RXPROFILEWEB",
          DeviceType: "RXPROFILEWEB"
        };
        
        console.log('API Request Body:', requestBody);
        
        const response = await fetch(getApiUrl('GET_PROFILE_DETAILS'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });
        
        if (!response.ok) {
          throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }
        
        // Get the raw response text first to check for JSON parsing issues
        const responseText = await response.text();
        console.log('Raw API Response:', responseText);
        
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error('JSON Parse Error:', parseError);
          console.error('Response that failed to parse:', responseText);
          throw new Error(`Invalid JSON response from server: ${parseError instanceof Error ? parseError.message : 'Unknown parsing error'}`);
        }
        
        console.log('API Response:', data);
        
        if (data.ResponseCode !== '00' && data.ResponseCode !== '0') {
          throw new Error(`API Error (${data.ResponseCode}): ${data.ResponseMessage}`);
        }
        
        const profile = data.OutputData[0];
        if (!profile) {
          throw new Error('No profile data found in API response');
        }
        
        console.log('Successfully loaded data from API');
        
        // Just use the profile data as-is from the API
        // The backend now includes OCRDetails with base64 images
        setProfileData(profile);
        
      } catch (error) {
        console.error('Error fetching profile data:', error);
        setProfileData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, [profileId]);

  // Fetch additional patron data (visitor_id, profile_type) from our backend
  useEffect(() => {
    const fetchPatronProfile = async () => {
      const lookupId = profileData?.MID || profileData?.MasterProfileID || profileId
      if (!lookupId) return
      try {
        const resp = await authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(lookupId)}/overview`))
        const data = await resp.json()
        if (resp.ok && data?.success && data?.data?.profile) {
          setPatronProfile(data.data.profile)
        }
      } catch (_) {
        // Non-critical: profile may not exist in patron_profiles yet
      }
    }
    if (profileData) fetchPatronProfile()
  }, [profileData?.MID, profileData?.MasterProfileID, profileId])

  const handleRecalculateRisk = async () => {
    const memberId = profileData?.MID
    if (!memberId) return
    setRiskRecalculating(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/profiles/${encodeURIComponent(memberId)}/recalculate-risk`),
        { method: 'POST' }
      )
      const json = await res.json()
      if (!res.ok || !json.success) {
        throw new Error(json.error || json.message || 'Failed to recalculate risk')
      }
      const d = json.data || {}
      setProfileData((prev) => prev ? {
        ...prev,
        RiskScore: d.risk_score ?? prev.RiskScore,
        RiskLevel: d.risk_level ?? prev.RiskLevel,
        RiskCategory: d.risk_level ?? prev.RiskCategory,
        RiskFactors: d.risk_factors ?? prev.RiskFactors,
        RiskAssessedAt: new Date().toISOString(),
      } : prev)
      toast({
        title: 'Risk assessment updated',
        description: `${d.risk_level || '—'} · ${d.risk_score ?? '—'}/100`,
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not recalculate risk',
        description: err?.message || 'Unknown error',
      })
    } finally {
      setRiskRecalculating(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center space-y-4">
          <RefreshCcw className="h-12 w-12 animate-spin text-primary" />
          <p className="text-lg font-medium">Loading profile data...</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="p-6">
        <Button variant="outline" onClick={onBack} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Search
        </Button>
        <Card>
          <CardContent className="py-10">
            <div className="flex flex-col items-center justify-center">
              <AlertTriangle className="h-16 w-16 text-red-500 mb-4" />
              <p className="text-xl font-semibold text-gray-900">Failed to Load Profile</p>
              <p className="text-sm text-gray-500 mt-2">
                Could not load profile data from API. An error popup was shown with details.
              </p>
              <div className="mt-4 space-x-2">
                <Button variant="outline" onClick={() => window.location.reload()}>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
                <Button onClick={onBack}>Return to Search</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const ocrDataUrlFallback = firstOcrDataUrl(profileData.OCRDetails);
  const verifyB64Display = (profileData.VerifyFPPhotoB64 || '').trim() || ocrDataUrlFallback;
  const idfpB64Display = (profileData.IDFPPhotoB64 || '').trim() || ocrDataUrlFallback;

  // Base skeleton structure of the profile detail page
  return (
    <div className="p-6 space-y-6">

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Search
        </Button>
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            onClick={fetchMembershipDetails}
            disabled={isLoadingMembership}
          >
            {isLoadingMembership ? (
              <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <User className="mr-2 h-4 w-4" />
            )}
            Membership Details
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowTransactionGamingProfile(true)}
            className="bg-gradient-to-r from-blue-50 to-teal-50 hover:from-blue-100 hover:to-teal-100"
          >
            <Activity className="mr-2 h-4 w-4" />
            View Transactions
          </Button>
          <Button
            onClick={runAmlPepCheck}
            disabled={isRunningAmlCheck}
          >
            {isRunningAmlCheck ? (
              <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <AlertCircle className="mr-2 h-4 w-4" />
            )}
            Run AML/PEP Check
          </Button>
        </div>
      </div>

      {/* Profile Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start space-x-6">
            <div className="h-24 w-24 overflow-hidden rounded-full border bg-gray-100 flex items-center justify-center">
              {verifyB64Display ? (
                <SmartImage
                  base64Data={verifyB64Display}
                  alt={`${profileData.FN} ${profileData.LN}`}
                  className="h-24 w-24 object-cover"
                  placeholderType="faceprint"
                  clickable={true}
                  modalTitle={`${profileData.FN} ${profileData.LN}`}
                />
              ) : (
                <Camera className="h-10 w-10 text-gray-400" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{`${profileData.FN} ${profileData.LN}`}</h2>
                  <div className="text-sm text-gray-500 flex items-center gap-2 flex-wrap mt-1">
                    {profileData.MasterProfileID && (
                      <span className="flex items-center gap-1">
                        <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{profileData.MasterProfileID}</span>
                        <button
                          onClick={(e) => copyToClipboard(profileData.MasterProfileID, e)}
                          className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Copy Patron ID"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {profileData.RiskLevel && (
                      <Badge className={`
                        ${profileData.RiskLevel === 'CRITICAL' ? 'bg-red-100 text-red-800 border-red-300' : ''}
                        ${profileData.RiskLevel === 'HIGH' ? 'bg-orange-100 text-orange-800 border-orange-300' : ''}
                        ${profileData.RiskLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' : ''}
                        ${profileData.RiskLevel === 'LOW' ? 'bg-green-100 text-green-800 border-green-300' : ''}
                        border
                      `}>
                        {profileData.RiskLevel === 'CRITICAL' && '🔴'}
                        {profileData.RiskLevel === 'HIGH' && '🟠'}
                        {profileData.RiskLevel === 'MEDIUM' && '🟡'}
                        {profileData.RiskLevel === 'LOW' && '🟢'}
                        {' '}{profileData.RiskLevel} Risk {profileData.RiskScore ? `(${profileData.RiskScore})` : ''}
                      </Badge>
                    )}
                    {profileData.RiskCategory && (
                      <Badge variant="outline" className="text-xs">
                        {profileData.RiskCategory}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="text-right space-y-2">
                  <div>
                    <button
                      className="btn-primary text-sm"
                      onClick={addToWatchlist}
                      title="Add this person to Watchlist"
                    >Add to Watchlist</button>
                  </div>
                  <div className="text-sm font-medium text-gray-500">Current Address</div>
                  <div className="font-medium">
                    {profileData.CurrentAddress?.Address || profileData.PrimaryAddress?.Address ? (
                      <>
                        <p>{profileData.CurrentAddress?.Address || profileData.PrimaryAddress?.Address}</p>
                        <p>{profileData.CurrentAddress?.Suburb || profileData.PrimaryAddress?.Suburb}, {profileData.CurrentAddress?.State || profileData.PrimaryAddress?.State} {profileData.CurrentAddress?.PostalCode || profileData.PrimaryAddress?.PostalCode}</p>
                      </>
                    ) : (
                      <p>No address on record</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-x-8 gap-y-4">
                <div className="flex-shrink-0">
                  <p className="text-sm font-medium text-gray-500">Date of Birth</p>
                  <p className="text-sm font-semibold">{profileData.DOB}</p>
                </div>
                <div className="flex-shrink-0">
                  <p className="text-sm font-medium text-gray-500">Last Updated</p>
                  <p className="text-sm font-semibold">{formatDateTime(patronProfile?.last_import_at || patronProfile?.updated_at || profileData.LUT)}</p>
                </div>
                {profileData.MID && (
                <div className="flex-shrink-0">
                  <p className="text-sm font-medium text-gray-500">Member ID</p>
                  <p className="text-sm font-semibold flex items-center gap-1">
                    <Users className="h-3 w-3 text-blue-600" />
                    <span className="font-mono text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{profileData.MID}</span>
                  </p>
                </div>
                )}
                {patronProfile?.visitor_id && (
                <div className="flex-shrink-0">
                  <p className="text-sm font-medium text-gray-500">Visitor ID</p>
                  <p className="text-sm font-semibold flex items-center gap-1">
                    <User className="h-3 w-3 text-amber-600" />
                    <span className="font-mono text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">{patronProfile.visitor_id}</span>
                  </p>
                </div>
                )}
                <div className="flex-shrink-0">
                  <p className="text-sm font-medium text-gray-500">KYC Status</p>
                  <Badge className={`text-xs ${getDVSStatusBadgeStyle(profileData.DVSStatus || 'Unknown')}`}>
                    {profileData.DVSStatus || 'Unknown'}
                  </Badge>
                </div>
                <div className="flex-shrink-0">
                  <p className="text-sm font-medium text-gray-500">AML/PEP Status</p>
                  <Badge className={`text-xs ${getAmlStatusBadgeStyle(amlHeaderStatus || 'N/A')}`}>
                    {amlHeaderStatus || 'N/A'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Risk Assessment Details */}
      {profileData.RiskLevel && (
        <Card className="border-l-4 border-l-orange-400">
          <CardHeader 
            className="cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setIsRiskAssessmentExpanded(!isRiskAssessmentExpanded)}
          >
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="mr-2">⚠️</span>
                Risk Assessment
                {profileData.RiskAssessedAt && (
                  <span className="ml-4 text-xs font-normal text-gray-500">
                    Assessed: {new Date(profileData.RiskAssessedAt).toLocaleDateString()}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs h-8"
                  disabled={riskRecalculating || !profileData?.MID}
                  onClick={(e) => { e.stopPropagation(); handleRecalculateRisk() }}
                >
                  <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${riskRecalculating ? 'animate-spin' : ''}`} />
                  {riskRecalculating ? 'Recalculating…' : 'Recalculate'}
                </Button>
                {/* Quick summary badges */}
                <Badge className={`
                  ${profileData.RiskLevel === 'CRITICAL' ? 'bg-red-100 text-red-800 border-red-300' : ''}
                  ${profileData.RiskLevel === 'HIGH' ? 'bg-orange-100 text-orange-800 border-orange-300' : ''}
                  ${profileData.RiskLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' : ''}
                  ${profileData.RiskLevel === 'LOW' ? 'bg-green-100 text-green-800 border-green-300' : ''}
                  border
                `}>
                  {profileData.RiskLevel}
                </Badge>
                <span className="text-sm font-semibold text-gray-700">
                  {profileData.RiskScore}/100
                </span>
                {isRiskAssessmentExpanded ? (
                  <ChevronUp className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                )}
              </div>
            </CardTitle>
          </CardHeader>
          
          {isRiskAssessmentExpanded && (
            <CardContent>
              {/* S2 — CDD intensity tier card */}
              <div className="mb-6">
                <CddTierCard memberId={profileData.MID || profileId} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="p-4 rounded-lg bg-gray-50">
                  <p className="text-sm font-medium text-gray-500 mb-1">Risk Level</p>
                  <div className="flex items-center">
                    <Badge className={`
                      text-lg py-1 px-3
                      ${profileData.RiskLevel === 'CRITICAL' ? 'bg-red-100 text-red-800 border-red-300' : ''}
                      ${profileData.RiskLevel === 'HIGH' ? 'bg-orange-100 text-orange-800 border-orange-300' : ''}
                      ${profileData.RiskLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' : ''}
                      ${profileData.RiskLevel === 'LOW' ? 'bg-green-100 text-green-800 border-green-300' : ''}
                      border
                    `}>
                      {profileData.RiskLevel}
                    </Badge>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-gray-50">
                  <p className="text-sm font-medium text-gray-500 mb-1">Risk Score</p>
                  <p className="text-2xl font-bold text-gray-900">{profileData.RiskScore || 0}<span className="text-sm text-gray-500">/100</span></p>
                </div>
                <div className="p-4 rounded-lg bg-gray-50">
                  <p className="text-sm font-medium text-gray-500 mb-1">Risk Category</p>
                  <p className="text-lg font-semibold text-gray-900">{profileData.RiskCategory || 'N/A'}</p>
                </div>
              </div>
              
              {profileData.RiskFactors && profileData.RiskFactors.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Contributing Risk Factors:</h4>
                  <div className="space-y-2">
                    {profileData.RiskFactors.map((factor, idx) => (
                      <div key={idx} className="flex items-start p-3 rounded bg-orange-50 border border-orange-200">
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-sm text-gray-900">
                                {factor.factor.replace(/_/g, ' ')}
                              </p>
                              <p className="text-xs text-gray-600 mt-1">{factor.detail}</p>
                            </div>
                            <Badge variant="outline" className="text-xs ml-2">
                              +{factor.score} pts
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Investigations & Cases */}
      <Card>
        <CardHeader>
          <CardTitle>Investigations & Cases</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {caseHistory.filter((c) => ['OPEN', 'INVESTIGATING', 'PENDING_REVIEW'].includes(String(c.status || '').toUpperCase())).length} ongoing
          </p>
        </CardHeader>
        <CardContent>
          {isLoadingCases ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <RefreshCcw className="h-4 w-4 animate-spin" />
              Loading case history...
            </div>
          ) : caseHistory.length === 0 ? (
            <p className="text-sm text-gray-500">No investigations or ECDD/SMR cases on record.</p>
          ) : (
            <div className="space-y-2">
              {caseHistory.map((c) => {
                const caseType = String(c.case_type || '').toUpperCase();
                const isInvestigation = caseType === 'INVESTIGATION';
                const isEcdd = caseType === 'ECDD';
                const isSmr = caseType === 'SMR';
                const href = isInvestigation
                  ? `/compliance-safe/investigations/${c.id}`
                  : isEcdd
                  ? `/compliance-safe/ecdd/${c.id}`
                  : isSmr
                  ? `/compliance-safe/investigations/${c.id}`
                  : null;
                return (
                  <div
                    key={c.id}
                    className={`flex items-center justify-between border rounded-lg p-3 ${href ? 'hover:bg-gray-50' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <Badge
                        className={
                          caseType === 'SMR'
                            ? 'bg-amber-100 text-amber-800 border-amber-200'
                            : caseType === 'ECDD'
                            ? 'bg-blue-100 text-blue-800 border-blue-200'
                            : caseType === 'INVESTIGATION'
                            ? 'bg-teal-100 text-teal-800 border-teal-200'
                            : 'bg-gray-100 text-gray-800 border-gray-200'
                        }
                      >
                        {c.case_type || 'Case'}
                      </Badge>
                      {href ? (
                        <Link to={href} className="font-medium text-sm text-primary hover:underline flex items-center gap-1">
                          {c.case_number || c.id}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      ) : (
                        <span className="font-medium text-sm">{c.case_number || c.id}</span>
                      )}
                      <span className="text-xs text-muted-foreground font-normal">
                        {c.status || '—'}
                      </span>
                    </div>
                    <div className="text-right text-xs text-gray-500">
                      {formatDateTime(c.created_at)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>


      {/* Document Details Section */}
      <div className="space-y-6 mt-8">
        
        {/* Document received for Profile creation */}
        {(profileData?.OCRDetails && profileData.OCRDetails.length > 0) && 
         (profileData?.CreatedDocumentDetails && profileData.CreatedDocumentDetails.length > 0) ? (
          <DocumentDetailPanel
            title="Document received for Profile creation"
            ocrDetails={profileData.OCRDetails}
            documentDetails={profileData.CreatedDocumentDetails}
            lastUpdatedTime={profileData?.CreatedDT}
            capturedAt={profileData?.CreatedDT}
            imageAvailable={true}
            editable={true}
            onSave={() => {}}
            onSubmitDvs={() => {}}
          />
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-md">
            <p className="text-gray-500">No profile creation document data available.</p>
          </div>
        )}

        {/* Document received for DVS check */}
        {(profileData?.RecentOCRDetails && profileData.RecentOCRDetails.length > 0) && 
         (profileData?.RecentDocumentDetails && profileData.RecentDocumentDetails.length > 0) ? (
          <DocumentDetailPanel
            title="Document received for DVS check"
            ocrDetails={profileData.RecentOCRDetails}
            documentDetails={profileData.RecentDocumentDetails}
            lastUpdatedTime={profileData?.LUT}
            capturedAt={profileData?.LUT}
            imageAvailable={true}
            editable={true}
            onSave={() => {}}
            onSubmitDvs={() => {}}
          />
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-md">
            <p className="text-gray-500">No recent document data available.</p>
          </div>
        )}
      </div>
    
      {/* AML Checks History */}
      <div className="space-y-2 mt-6">
        <Card>
          <CardHeader>
            <CardTitle>AML/PEP Checks</CardTitle>
          </CardHeader>
          <CardContent>
            {amlChecks.length === 0 ? (
              <p className="text-sm text-gray-500">No AML checks recorded.</p>
            ) : (
              <div className="space-y-2">
                {amlChecks.map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-sm border rounded p-2">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{c.status}</Badge>
                      <span>{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right mr-2">
                        <span className="font-medium">Results: {c.result_count}</span>
                        {c.reason && <div className="text-gray-500">{c.reason}</div>}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => viewAmlCheckDetails(c.id)}>View</Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const url = new URL(window.location.href);
                          url.searchParams.set('aml_check_id', String(c.id));
                          navigator.clipboard.writeText(url.toString());
                          toast({ title: 'Link copied', description: `AML check #${c.id} link copied to clipboard` });
                        }}
                      >Link</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Other Identity Documents Used */}
      <div className="space-y-2 mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Other Identity Documents Used</CardTitle>
          </CardHeader>
          <CardContent>
            {otherDocuments.length === 0 ? (
              <p className="text-sm text-gray-500">No additional documents on record.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600">
                      <th className="p-2">Type</th>
                      <th className="p-2">Number</th>
                      <th className="p-2">Issuer</th>
                      <th className="p-2">Used For</th>
                      <th className="p-2">Captured At</th>
                      <th className="p-2">Image</th>
                      <th className="p-2">Status</th>
                      <th className="p-2">Aging</th>
                    </tr>
                  </thead>
                  <tbody>
                    {otherDocuments.map((d) => {
                      const cap = d.captured_at ? new Date(d.captured_at) : null;
                      const exp = cap ? new Date(cap.getTime() + 7*24*60*60*1000) : null;
                      const now = new Date();
                      let chip = 'N/A';
                      let cls = 'bg-gray-100 text-gray-800 border-gray-200';
                      if (exp) {
                        if (now > exp) { chip = 'Image expired'; cls = 'bg-red-100 text-red-800 border-red-200'; }
                        else {
                          const daysLeft = Math.ceil((exp.getTime() - now.getTime())/(24*60*60*1000));
                          chip = `${daysLeft} day${daysLeft===1?'':'s'} left`;
                          cls = daysLeft>3 ? 'bg-green-100 text-green-800 border-green-200' : daysLeft>=1 ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : 'bg-red-100 text-red-800 border-red-200';
                        }
                      }
                      return (
                        <tr key={`${d.source || 'id'}_${d.id}`} className="border-t">
                          <td className="p-2">
                            <div>{d.doc_type || d.kind || 'N/A'}</div>
                            {d.file_name && <div className="text-xs text-gray-500 truncate max-w-[12rem]">{d.file_name}</div>}
                          </td>
                          <td className="p-2">{d.doc_number_masked || '—'}</td>
                          <td className="p-2">{d.issuing_country || '—'}</td>
                          <td className="p-2">{d.used_for || '—'}</td>
                          <td className="p-2">{d.captured_at ? new Date(d.captured_at).toLocaleString() : '—'}</td>
                          <td className="p-2">
                            {d.image_url ? (
                              <a href={d.image_url} target="_blank" rel="noopener noreferrer" className="text-psg-teal text-xs font-semibold hover:underline">View file</a>
                            ) : d.image_available ? (
                              <Badge className="bg-green-100 text-green-800 border-green-200">Available</Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-800 border-gray-200">No file</Badge>
                            )}
                          </td>
                          <td className="p-2">{d.status || 'N/A'}</td>
                          <td className="p-2"><Badge className={cls}>{chip}</Badge></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* AML Results Right Panel — portal to body so fixed positioning is not clipped by Layout main.overflow-y-auto */}
      {isAmlModalOpen && amlModalData && typeof document !== 'undefined' && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[100]"
            onClick={() => setIsAmlModalOpen(false)}
            aria-hidden="true"
          />

          {/* Sliding Panel */}
          <div className="fixed right-0 top-0 h-full w-[880px] max-w-full bg-white shadow-xl z-[110] transform transition-transform duration-300 ease-in-out">
            {/* Panel Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">{amlModalData.results[selectedAmlIndex]?.primaryName || amlModalData.name}</h3>
                <div className="text-sm text-gray-600">Check #{amlModalData.checkId}{amlModalData.country ? ` • ${amlModalData.country}` : ''}</div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setIsAmlModalOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Panel Content */}
            <div className="p-4 overflow-y-auto" style={{ height: 'calc(100vh - 64px)' }}>
              {(!amlModalData.results || amlModalData.results.length === 0) ? (
                <div className="space-y-3">
                  <div className="text-sm text-gray-500">No matches found.</div>
                  {amlChecks.filter((c) => c.result_count > 0).length > 0 && (
                    <div className="text-sm text-blue-600 border border-blue-200 rounded-lg p-3 bg-blue-50">
                      <strong>Previous checks with matches:</strong> Close this and click <strong>View</strong> on check #{amlChecks.filter((c) => c.result_count > 0).map((c) => c.id).slice(0, 3).join(', #')} in the AML/PEP Checks list below to see match details.
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Left: Risk & Basic Information (2 cols) */}
                  <div className="lg:col-span-2 space-y-4">
                    {/* Risk Assessment Card */}
                    {(() => {
                      const r = amlModalData.results[selectedAmlIndex] || {};
                      return (
                        <div className="border rounded-lg p-4 bg-orange-50/40">
                          <div className="font-semibold text-gray-900 mb-3 flex items-center">
                            <span className="mr-2">⚠️</span>
                            Risk Assessment
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="text-center">
                              <div className="inline-block px-3 py-2 text-sm font-bold rounded bg-orange-500 text-white">{r.riskLevel || 'High Risk'}</div>
                              <div className="text-xs text-gray-600 mt-1">Overall Risk Level</div>
                            </div>
                            <div className="text-center">
                              <div className="inline-block px-3 py-2 text-sm font-medium rounded border bg-purple-50 text-purple-800 border-purple-200">{formatThreatLevel(r.threatLevel) || '—'}</div>
                              <div className="text-xs text-gray-600 mt-1">Threat Classification</div>
                            </div>
                            <div className="text-center">
                              <div className="inline-block px-3 py-2 text-sm font-medium rounded border bg-red-50 text-red-800 border-red-200">{r.riskCategory || 'PEP'}</div>
                              <div className="text-xs text-gray-600 mt-1">Category</div>
                            </div>
                          </div>
                        </div>
                      )
                    })()}

                    {/* Basic Information Card */}
                    {(() => {
                      const r = amlModalData.results[selectedAmlIndex] || {};
                      return (
                        <div className="border rounded-lg p-4">
                          <div className="text-base font-semibold text-gray-900 mb-3">Basic Information</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              {r.matchKind && (
                                <div className="mb-2"><span className="text-gray-600">Match type:</span> <span className="ml-2 text-gray-900">{String(r.matchKind).replace(/_/g, ' ')}</span></div>
                              )}
                              <div className="mb-2"><span className="text-gray-600">Risk Category:</span> <span className="ml-2 inline-block px-2 py-1 text-xs rounded bg-red-50 text-red-800 border border-red-200">{r.riskCategory || 'PEP - Standard Risk Profile'}</span></div>
                              {Array.isArray(r.aliases) && r.aliases.length > 0 && (
                                <div className="mb-2"><span className="text-gray-600">Aliases:</span> <span className="ml-2">{r.aliases.join(', ')}</span></div>
                              )}
                              <div className="mb-2"><span className="text-gray-600">Match Confidence:</span> <span className="ml-2 font-semibold text-rose-600">{typeof r.confidence !== 'undefined' ? `${r.confidence}%` : '—'}</span></div>
                              {r.gender && (<div className="mb-2"><span className="text-gray-600">Gender:</span> <span className="ml-2">{r.gender}</span></div>)}
                              {r.nationality && (<div className="mb-2"><span className="text-gray-600">Nationality:</span> <span className="ml-2">{r.nationality}</span></div>)}
                              {typeof r.active !== 'undefined' && (<div className="mb-2"><span className="text-gray-600">Status:</span> <span className={`ml-2 ${r.active ? 'text-green-600' : 'text-red-600'}`}>{r.active ? 'Active' : 'Inactive'}</span></div>)}
                              {r.lastUpdated && (<div className="mb-2"><span className="text-gray-600">Last Updated:</span> <span className="ml-2">{r.lastUpdated}</span></div>)}
                            </div>
                            <div>
                              {Array.isArray(r.countries) && r.countries.length > 0 && (
                                <div className="mb-2"><span className="text-gray-600">Countries ({r.countries.length}):</span>
                                  <div className="mt-1 flex flex-wrap gap-2">{r.countries.map((c, i) => (<span key={i} className="text-xs bg-blue-50 text-blue-800 px-2 py-1 rounded border border-blue-200">{c}</span>))}</div>
                                </div>
                              )}
                              {Array.isArray(r.sanctions) && r.sanctions.length > 0 && (
                                <div className="mb-2"><span className="text-gray-600">Sanctions ({r.sanctions.length}):</span>
                                  <div className="mt-1 space-y-1">{r.sanctions.slice(0, 5).map((s, i) => (
                                    <div key={i} className="text-xs text-red-700 bg-red-50 px-2 py-1 rounded border border-red-200">⚠️ {s}</div>
                                  ))}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })()}

                    {/* Intelligence Profile */}
                    {(() => {
                      const r = amlModalData.results[selectedAmlIndex] || {};
                      if (!r.profileNotes) return null;
                      return (
                        <div className="border rounded-lg p-4 bg-amber-50">
                          <div className="text-base font-semibold text-gray-900 mb-2 flex items-center">
                            <span className="mr-2">📄</span>
                            Intelligence Profile
                          </div>
                          <div className="text-sm text-amber-800 leading-relaxed">
                            {String(sanitizeVendorBranding(r.profileNotes) || '').split('\n').map((line, i) => (
                              <p key={i} className={i>0 ? 'mt-2' : ''}>{line}</p>
                            ))}
                          </div>
                        </div>
                      )
                    })()}
                  </div>

                  {/* Right: Matches by category (tabs) */}
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-700">Matches ({amlModalData.results.length})</div>
                    <AmlCategoryResultsTabs
                      results={amlModalData.results}
                      resultsByCategory={amlModalData.resultsByCategory}
                      selectedIndex={selectedAmlIndex}
                      onSelectIndex={setSelectedAmlIndex}
                      compact
                      showProviderMeta
                      provider={amlModalData.provider}
                      greenidVerificationId={amlModalData.greenidVerificationId}
                      greenidOverallStatus={amlModalData.greenidOverallStatus}
                      screeningSummary={amlModalData.screeningSummary}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}

    {/* Membership Details Sliding Panel */}
    {showMembershipPanel && (
      <>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setShowMembershipPanel(false)}
        />
        
        {/* Panel */}
        <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out">
          {/* Panel Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold">Membership Details</h3>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowMembershipPanel(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Panel Content */}
          <div className="p-4 overflow-y-auto" style={{ height: 'calc(100vh - 80px)' }}>
            {membershipData && membershipData.PatronDetails ? (
              <div className="space-y-4">
                {/* Member Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center justify-between">
                      Member Details
                      <Badge variant={membershipData.PatronDetails.MembershipStatus === "ACTIVE" ? "default" : "secondary"}>
                        {membershipData.PatronDetails.MembershipStatus}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Personal Information */}
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2">Personal Information</h5>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Name:</span>
                          <p className="font-medium">{membershipData.PatronDetails.FirstName} {membershipData.PatronDetails.LastName}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Date of Birth:</span>
                          <p className="font-medium">{formatDateTime(membershipData.PatronDetails.DateOfBirth)}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Gender:</span>
                          <p className="font-medium">{membershipData.PatronDetails.Gender === 'M' ? 'Male' : membershipData.PatronDetails.Gender === 'F' ? 'Female' : membershipData.PatronDetails.Gender}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Occupation:</span>
                          <p className="font-medium">{membershipData.PatronDetails.Occupation || 'Not specified'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Membership Information */}
                    <div className="pt-3 border-t">
                      <h5 className="font-medium text-gray-900 mb-2">Membership Information</h5>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Member ID:</span>
                          <p className="font-medium">{membershipData.PatronDetails.MemberID}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Account ID:</span>
                          <p className="font-medium">{membershipData.PatronDetails.AccountID}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Card Number:</span>
                          <p className="font-medium">{membershipData.PatronDetails.CardNumber}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Tier Level:</span>
                          <p className="font-medium">
                            <Badge variant="outline" className="text-xs">
                              {membershipData.PatronDetails.TierLevel}
                            </Badge>
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-600">Membership Type:</span>
                          <p className="font-medium">{membershipData.PatronDetails.MembershipType}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Home Site:</span>
                          <p className="font-medium">{membershipData.PatronDetails.HomeSite}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Joined Date:</span>
                          <p className="font-medium">{formatDateTime(membershipData.PatronDetails.DateJoined)}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Last Visit:</span>
                          <p className="font-medium">{membershipData.PatronDetails.LastVisitDate ? formatDateTime(membershipData.PatronDetails.LastVisitDate) : 'Never'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="pt-3 border-t">
                      <h5 className="font-medium text-gray-900 mb-2">Contact Information</h5>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-600">Email:</span>
                          <p className="font-medium">{membershipData.PatronDetails.Email}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Mobile Phone:</span>
                          <p className="font-medium">{membershipData.PatronDetails.MobilePhone}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Address:</span>
                          <p className="font-medium">
                            {membershipData.PatronDetails.Address1}
                            {membershipData.PatronDetails.Address2 && <><br />{membershipData.PatronDetails.Address2}</>}
                            <br />
                            {membershipData.PatronDetails.Suburb}, {membershipData.PatronDetails.State} {membershipData.PatronDetails.PostCode}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* ID Document Information */}
                    <div className="pt-3 border-t">
                      <h5 className="font-medium text-gray-900 mb-2">ID Document</h5>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Document Type:</span>
                          <p className="font-medium">{membershipData.PatronDetails.IDDocumentType}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Document Number:</span>
                          <p className="font-medium">{membershipData.PatronDetails.IDDocumentNumber}</p>
                        </div>
                        {membershipData.PatronDetails.CountryofResidence && (
                          <div>
                            <span className="text-gray-600">Country of Residence:</span>
                            <p className="font-medium">{membershipData.PatronDetails.CountryofResidence}</p>
                          </div>
                        )}
                        {membershipData.PatronDetails.CountryofCitizenship && (
                          <div>
                            <span className="text-gray-600">Country of Citizenship:</span>
                            <p className="font-medium">{membershipData.PatronDetails.CountryofCitizenship}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No membership data available</p>
                {isLoadingMembership && <p className="mt-2">Loading...</p>}
              </div>
            )}
          </div>
        </div>
      </>
    )}

    {/* Transaction & Gaming Profile Modal */}
    {showTransactionGamingProfile && profileData && (
      <TransactionGamingProfile
        memberId={profileData.MID || String(profileId)}
        memberName={`${profileData.FN} ${profileData.LN}`}
        onClose={() => setShowTransactionGamingProfile(false)}
      />
    )}
    </div>
  );
};

export default ProfileDetail;